/* @jsx h */
import { defineComponent, h } from "vue";
import {
  RestaurantCustomer,
  RestaurantCustomerAddress as CustomerAddress,
  CreateCustomerAddressData,
} from "@lemmonhq/types";
import { CustomerAddressList } from "./CustomerAddressList";
import { CustomerAddressCreate } from "./CustomerAddressCreate";

export interface AddressStepProps {
  orderType: "delivery" | "takeaway";
  requiresAddress: boolean;
  selectedCustomer: RestaurantCustomer | null;
  addresses: CustomerAddress[];
  selectedAddressId: string | undefined;
  onSelectAddress: (id: string) => void;
  onRequestNewAddress: () => void;
  isLoadingAddresses: boolean;
  showAddressForm: boolean;
  addressForm: CreateCustomerAddressData;
  onUpdateAddressForm: (
    field: keyof CreateCustomerAddressData,
    value: string,
  ) => void;
  addressFormError: string;
  isCreatingAddress: boolean;
  onCreateAddress: () => void;
  onCancelAddress: () => void;
}

export const AddressStep = defineComponent<AddressStepProps>({
  name: "AddressStep",
  props: [
    "orderType",
    "requiresAddress",
    "selectedCustomer",
    "addresses",
    "selectedAddressId",
    "onSelectAddress",
    "onRequestNewAddress",
    "isLoadingAddresses",
    "showAddressForm",
    "addressForm",
    "onUpdateAddressForm",
    "addressFormError",
    "isCreatingAddress",
    "onCreateAddress",
    "onCancelAddress",
  ],
  setup(props) {
    return () => (
      <div class="card bg-base-100/50 shadow-lg border border-base-300">
        <div class="card-body space-y-6">
          <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 class="text-lg font-semibold mt-0 text-accent">
                {props.orderType === "delivery"
                  ? "Delivery details"
                  : "Pickup details"}
              </h3>
              <p class="text-base-content/70">
                {props.orderType === "delivery"
                  ? "Confirm or capture where we should deliver."
                  : "Pickup orders don't require an address."}
              </p>
            </div>
            {props.requiresAddress &&
              props.selectedCustomer &&
              !props.showAddressForm && (
                <button
                  class="btn btn-sm btn-outline"
                  onClick={props.onRequestNewAddress}
                >
                  <span class="material-symbols-outlined">
                    add_location_alt
                  </span>
                  New address
                </button>
              )}
          </div>

          {props.requiresAddress ? (
            props.selectedCustomer ? (
              <>
                <CustomerAddressList
                  hidden={props.showAddressForm}
                  addresses={props.addresses}
                  selectedAddressId={props.selectedAddressId}
                  onSelect={props.onSelectAddress}
                  onAddNew={props.onRequestNewAddress}
                  isLoading={props.isLoadingAddresses}
                />

                {props.addressFormError && !props.showAddressForm && (
                  <div class="alert alert-error">
                    <span class="material-symbols-outlined">error</span>
                    {props.addressFormError}
                  </div>
                )}

                {props.showAddressForm && (
                  <CustomerAddressCreate
                    form={props.addressForm}
                    onUpdate={props.onUpdateAddressForm}
                    onSubmit={props.onCreateAddress}
                    onCancel={props.onCancelAddress}
                    isSubmitting={props.isCreatingAddress}
                    errorMessage={props.addressFormError}
                  />
                )}
              </>
            ) : null
          ) : (
            <div class="rounded-2xl border border-info/30 bg-info/5 p-5 space-y-2">
              <div class="flex gap-3 items-start">
                <span class="material-symbols-outlined text-2xl text-info">
                  event_available
                </span>
                <div>
                  <h4 class="font-semibold text-info">Pickup order</h4>
                  <p class="text-sm text-info/70">
                    Addresses are optional for takeaway — we'll still keep
                    contact details handy.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  },
});
